import { ArrowRightCircleIcon, ArrowUpCircleIcon, BuildingOffice2Icon, CheckIcon, DevicePhoneMobileIcon, EnvelopeIcon, HomeIcon as HomeIconOutline, HomeModernIcon, KeyIcon, MapPinIcon, PhoneIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline"
import { HomeIcon, StarIcon } from "@heroicons/react/24/solid"
import { FC, useEffect, useState } from "react"
import { useMediaQuery } from "react-responsive"
import { useNavigate } from "react-router-dom"
import images from '../assets/loaders/imageLoader'
import DiffusedBackground from "../components/common/Images/DiffusedBackground"
import AnimateChildrenOnScroll from "../components/common/miscellaneous/AnimateChildrenOnScroll"
import FloatingBadge from "../components/common/miscellaneous/FloatingBadge"
import { Alert, ChatBubble } from "../components/daisy"
import { breakpoints } from "../constants/breakpoints"
import useRapidCounter from "../hooks/useRapidCounter"
import { useResponsiveWidth } from "../hooks/useResponsiveWidth"
import useSmoothScroll from "../hooks/useSmoothScrolling"
import useTheme from "../hooks/useTheme"
import { Paths } from "../routes/paths"
import { FixedProjects } from "./ProjectsPage"
import useScrollToTop from "../hooks/useScrollToTop"
import { AnimatePresence, motion } from "framer-motion"

const HomePage: FC = () => {
  const { changeTheme } = useTheme()
  const { showScrollTop, scrollToTop } = useScrollToTop({ threshold: 200 })
  useSmoothScroll()

  useEffect(() => {
    changeTheme("dark")
  }, [])

  return (
    <main className="">
      <AnimateChildrenOnScroll
        animationClass="fade-in-up"
        threshold={0.1}
        className="duration-1000"
      >
        <Hero />
        <Projects />
        <AboutUs />
        <Location />
        <ContactUs />
      </AnimateChildrenOnScroll>
      <AnimatePresence>
        {showScrollTop && (
          <>
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToTop}
              className={`fixed right-6 bottom-4 z-50 p-2 rounded-full 
            bg-ghost text-primary-content shadow-lg hover:bg-neutral transition-colors duration-200`}
              aria-label="Scroll to top"
            >
              <ArrowUpCircleIcon className="w-8 text-info" title="Volver arriba" />
            </motion.button>
          </>
        )}
      </AnimatePresence>
    </main>
  )
}

const Hero = () => {
  return (
    <section className="relative" id="main" role="banner" aria-label="Inicio">
      <div className="block sm:hidden min-h-screen">
        <DiffusedBackground
          className="rounded-lg w-full h-screen"
          image={images.portrait2}
          alt="Landing Image"
          blurAmount={8}
          opacity={0.5}
          scale={1.1}
        />
      </div>
      <div className="hidden sm:block">
        <img
          src={images.portrait2}
          alt="Landing Image"
          className="w-full object-cover"
        />
      </div>
      <div className="z-30 absolute inset-0 bg-black bg-opacity-40">
        <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/4">
          <img src={images.logoCompleteDark} alt="Logo" className="w-80" />
        </div>
        <div className="absolute top-3/4 sm:top-2/3 left-1/2 transform -translate-x-1/2">
          <a href="#projects" className="bg-yellow-500 hover:bg-yellow-600 text-center text-white font-semibold px-8 py-3 rounded-full">
            Ver Proyectos
          </a>
        </div>
      </div>
      {/* Structured Data for Breadcrumb */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": "https://tierrasdelparana.com.ar/"
          }]
        })}
      </script>
    </section >
  )
}

interface ProjectProps {
  image: string
  name: string
  description: React.ReactNode,
  floatingBadge: React.ReactNode
  location: string
  finished?: boolean
  routeTo?: string
}

const Project: FC<ProjectProps> = ({ image, name, description, floatingBadge, location, finished = false, routeTo }) => {
  const navigate = useNavigate()
  return (
    <div className={`max-w-lg mx-auto bg-white rounded-lg shadow-md overflow-hidden relative
      ${finished ? "" : "sm:hover:shadow-xl sm:hover:scale-105 sm:transition-all sm:duration-300 sm:cursor-pointer"}
    `}
      onClick={() => routeTo && navigate(routeTo)}>
      <div className="flex flex-col h-full justify-between">
        <img
          loading="lazy"
          src={image}
          alt={name}
          className="w-full h-48 object-cover"
        />
        <div className="py-4 px-6">
          <h3 className={`text-xl font-semibold text-gray-900 mb-3 ${!finished ? "uppercase" : ""}`}>
            {name}
          </h3>
          <div className="text-gray-600">
            {description}
          </div>
        </div>
        <div className="p-2 flex justify-between">
          <a href={location} target="_blank" className="btn btn-sm btn-neutral text-black">
            <div className="flex items-center gap-1 text-black">
              <MapPinIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold text-sm">Ubicación</span>
            </div>
          </a>
          {!finished &&
            <a href={routeTo} className="max-w-[12rem] btn btn-sm btn-neutral text-black block sm:hidden">
              <div className="h-full flex items-center gap-1 text-black">
                <span className="font-semibold text-sm">Más detalles</span>
                <ArrowRightCircleIcon className="w-5 flex-shrink-0" />
              </div>
            </a>}
          {!finished &&
            <a href={routeTo} className="btn btn-sm btn-neutral justify-self-end self-end hidden sm:block">
              <div className="h-full flex items-center gap-1 text-black">
                <span className="font-semibold text-sm">Más detalles</span>
                <ArrowRightCircleIcon className="w-5 flex-shrink-0" />
              </div>
            </a>}
        </div>
      </div>
      {floatingBadge}
    </div>
  )
}

const Projects = () => {
  const navigate = useNavigate()
  return (
    <section className="px-4 lg:px-12 py-12" id="projects">
      <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
        Nuestros Proyectos Activos
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Project
          image={images.LopezJordan1}
          name="Lopez Jordan al Río 2"
          location="https://maps.app.goo.gl/qg6cDNu9EbSjLBfM7"
          routeTo={Paths.projects + "/" + FixedProjects.LOPEZ_JORDAN}
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-primary" />
                Complejo de viviendas con una vista al río privilegiada, rodeado de un ambiente natural.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-primary" />
                Ideal para quienes buscan un ambiente amplio y desconectado, a 15 minutos del centro.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-primary px-3">
            <div className="flex items-center gap-2 text-success-content">
              <KeyIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">En construcción</span>
            </div>
          </FloatingBadge>}
        />
        <Project
          image={images.jujuyRender1}
          name="Jujuy 006"
          location="https://maps.app.goo.gl/V1dHMQewEbLYzL1r5"
          routeTo={Paths.projects + "/" + FixedProjects.JUJUY}
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-secondary" />
                Edificio de 7 pisos con un total de 14 departamentos y local comercial en planta baja.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-secondary" />
                Ideal para vivir cómodo o para inversión, a minutos del centro.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-secondary px-3">
            <div className="flex items-center gap-2 text-success-content">
              <KeyIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">En construcción</span>
            </div>
          </FloatingBadge>}
        />
      </div>
      <h2 className="text-3xl font-bold text-gray-900 my-12 text-center italic">
        Nuestra historia, proyectos finalizados
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        <Project
          image={images.pascual1}
          name="Pascual 005"
          finished
          location="https://maps.app.goo.gl/8qaipRk83zQ32GoNA"
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Edificio de 7 pisos, desarrollado también en planta baja, constituyen 13 departamentos con cocheras.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Distribución en monoambientes y de 1 dormitorio, dentro del casco céntrico.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-success px-3">
            <div className="flex items-center gap-1 text-success-content">
              <CheckIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">Finalizado</span>
            </div>
          </FloatingBadge>}
        />
        <Project
          image={images.diamente1}
          name="Diamante 004"
          location="https://maps.app.goo.gl/Yzg13Zfm88fgwiX56"
          finished
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Edificio desarrollado en 3 pisos y planta baja, con un total 8 unidades habitacionales.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Diseñado en frente y contrafrente, a 3 minutos del centro de la ciudad.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-success px-3">
            <div className="flex items-center gap-1 text-success-content">
              <CheckIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">Finalizado</span>
            </div>
          </FloatingBadge>}

        />
        <Project
          image={images.guemes1}
          name="Guemes 003"
          location="https://maps.app.goo.gl/FGsof6qXKzQpQhXi7"
          finished
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Edificio multifamiliar, compuesto de 8 departamentos distribuídos en planta baja y 3 pisos.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Diseño en frente y contrafrente, con tipología de monoambiente, un dormitorio y dos dormitorios.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-success px-3">
            <div className="flex items-center gap-1 text-success-content">
              <CheckIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">Finalizado</span>
            </div>
          </FloatingBadge>}
        />
        <Project
          image={images.blas1}
          name="Complejo Blas 002"
          location="https://maps.app.goo.gl/v9bJLm2LpAgJwtHC8"
          finished
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Complejo habitacional de 19 unidades funcionales de 2 y 3 dormitorios, espacios comunes y amenities.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Pensado y desarrollado para llevar una vida simple, segura, de calidad y disfrute.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-success px-3">
            <div className="flex items-center gap-1 text-success-content">
              <CheckIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">Finalizado</span>
            </div>
          </FloatingBadge>}
        />
        <Project
          image={images.echeverria1}
          name="Echeverría 001"
          location="https://maps.app.goo.gl/Nr1sq5TNRQNQoXza9"
          finished
          description={
            <ul className="list-disc list-inside space-y-1 marker:text-xs">
              <li className="flex items-start gap-2">
                <HomeIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Conjunto de 9 unidades funcionales con espacio verde, que constan de 3 niveles para lograr extraordinarias vistas a la ciudad.
              </li>
              <li className="flex items-start gap-2">
                <StarIcon className="w-6 flex-shrink-0 mt-0 text-success" />
                Duplex de 2 dormitorios, cochera doble, con living comedor, baño, terraza y churrasquera.
              </li>
            </ul>
          }
          floatingBadge={<FloatingBadge className="bg-success px-3">
            <div className="flex items-center gap-1 text-success-content">
              <CheckIcon className="w-5 flex-shrink-0" />
              <span className="font-semibold">Finalizado</span>
            </div>
          </FloatingBadge>}
        />
      </div>
    </section>
  )
}

const AboutUs = () => {
  const { currentValue: unitsBuilt, ref: unitsBuiltRef } = useRapidCounter(
    {
      initialValue: 0,
      finalValue: 90,
      duration: 1500,
      intersectionThreshold: 0.1
    }
  );
  const { currentValue: squareMeters, ref: squareMetersRef } = useRapidCounter(
    {
      initialValue: 0,
      finalValue: 7500,
      duration: 1500,
      intersectionThreshold: 0.1
    }
  );
  return (
    <section className="relative py-12" id="about-us">
      <div className="absolute inset-0">
        <img src={images.backgroundImage1} alt="Background" loading="lazy" className="w-full h-full object-cover" />
      </div>
      <div className="relative z-10 container mx-auto px-4 sm:px-8">
        <div className="flex items-center lg:flex-row flex-col gap-10 lg:gap-16">
          <div className="grid grid-cols-1 gap-8 lg:gap-5">
            <div className="text-neutral-content">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 border-b border-neutral pb-2">Nosotros</h2>
              <div className="space-y-6">
                <p>
                  Somos una desarrolladora inmobiliaria con más de 15 años de experiencia en Paraná, Entre Ríos. Nos dedicamos al desarrollo, dirección y construcción de obras, acompañando cada proyecto desde el inicio hasta su entrega final.
                </p>
                <p>
                  Trabajamos con un equipo sólido y comprometido, enfocado en crear espacios que mejoren la vida de las personas y aporten valor a la ciudad. Nos gusta hacer las cosas bien, con seriedad, responsabilidad y atención a cada detalle.
                </p>
                <p>
                  Creemos en el crecimiento a largo plazo y en construir con una visión clara: <span className="font-semibold">proyectos que perduren y hagan la diferencia</span>.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-5 justify-center">
              <div className="grid grid-cols-3 gap-5 items-center w-full">
                <div className="w-16 h-16 rounded-full bg-success flex items-center justify-center ml-auto">
                  <span className="font-semibold font-mono" ref={unitsBuiltRef}>+{unitsBuilt}</span>
                </div>
                <span className="font-semibold font-mono flex gap-3 items-center">
                  UNIDADES CONSTRUIDAS
                  <HomeModernIcon className="w-6 flex-shrink-0" />
                </span>
              </div>
              <div className="grid grid-cols-3 gap-5 items-center w-full">
                <div className="w-16 h-16 rounded-full bg-success flex items-center justify-center ml-auto">
                  <span className="font-semibold font-mono" ref={squareMetersRef}>+{squareMeters}</span>
                </div>
                <span className="font-semibold font-mono flex gap-3 items-center">
                  M2 CONSTRUCTIVOS
                  <WrenchScrewdriverIcon className="w-6 flex-shrink-0" />
                </span>
              </div>
            </div>
          </div>
          <img src={images.logoHorizontalDark} alt="Logo" loading="lazy" className="w-96 mb-12" />
        </div>
      </div>
    </section >
  )
}

enum FixedLocation {
  JUJUY = 1,
  LOPEZ_JORDAN = 2
}

const Location = () => {
  const width = useResponsiveWidth({ query: breakpoints.sm, fixedWidth: 600 });
  const isTablet = useMediaQuery({ query: breakpoints.lg });
  const [selectedLocation, setSelectedLocation] = useState<FixedLocation>(FixedLocation.LOPEZ_JORDAN);
  return (
    <section className="relative py-12" id="location">
      <div className="absolute inset-0 ">
        {selectedLocation === FixedLocation.LOPEZ_JORDAN &&
          <img src={images.backgroundImage2}
            loading="lazy"
            alt="LOPEZ_JORDAN"
            className="w-full h-full object-cover animate-fade-in opacity-0 transition-opacity duration-1000" />}
        {selectedLocation === FixedLocation.JUJUY &&
          <img src={images.jujuyPanoramica}
            loading="lazy"
            alt="JUJUY"
            className="w-full h-full object-cover animate-fade-in opacity-0 transition-opacity duration-1000" />}
      </div>
      <div className="relative z-10 container mx-auto px-4 sm:px-8">
        <div className="text-center mb-8">
          <h2 className="text-black text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 leading-tight px-4">
            Ubicación de Nuestros Proyectos
          </h2>
          <div className="bg-success h-1 lg:h-1.5 w-80 mx-auto mt-2 sm:mt-5 "></div>
        </div>
        <div className="flex flex-col items-center gap-5">
          <div className="join">
            <button className={`btn join-item btn-md md:btn-lg text-secondary-content
                ${selectedLocation === FixedLocation.LOPEZ_JORDAN ? "btn-secondary" : "btn-neutral"}`}
              onClick={(e) => { e.preventDefault(); setSelectedLocation(FixedLocation.LOPEZ_JORDAN) }}>
              <MapPinIcon className="w-6 mr-2 flex-shrink-0" />
              <span className="text-normal text-sm sm:text-base">López Jordan
                <span className="hidden sm:inline"> y Pintor Nux</span>
              </span>
            </button>
            <button className={`btn join-item btn-md md:btn-lg text-secondary-content
                ${selectedLocation === FixedLocation.JUJUY ? "btn-secondary" : "btn-neutral"}`}
              onClick={(e) => { e.preventDefault(); setSelectedLocation(FixedLocation.JUJUY) }}>
              <MapPinIcon className="w-6 mr-2 flex-shrink-0" />
              <span className="text-normal text-sm sm:text-base">Jujuy
                <span className="hidden sm:inline"> y Olegorio Victor Andrade</span>
              </span>
            </button>
          </div>
          <div className="flex flex-col gap-10">
          </div>
          {selectedLocation === FixedLocation.LOPEZ_JORDAN &&
            <div className="flex flex-col lg:flex-row items-center gap-5">
              {!isTablet && <ChatBubble className="max-w-lg mx-auto animate-fade-in-up opacity-0 transition-opacity duration-1000" end>
                <ChatBubble.Message color="primary">
                  <div className="flex justify-center items-center gap-1 mb-4">
                    <HomeIconOutline className="w-6 mr-2 flex-shrink-0" />
                    <h3 className="text-normal md:text-lg font-semibold">
                      Tu casa frente al río
                    </h3>
                  </div>
                  <span className="text-sm md:text-base">Emplazado en la zona más emergente de la ciudad de Paraná,
                    a 15 minutos del centro de la misma con dos accesos directos,
                    uno por Blas Parera y el otro por el acceso norte.</span>
                </ChatBubble.Message>
              </ChatBubble>}
              {isTablet &&
                <Alert className="bg-primary border-primary text-primary-content flex flex-col gap-1 mx-4 sm:mx-auto max-w-xs sm:max-w-lg animate-fade-in-up opacity-0 transition-opacity duration-1000">
                  <div className="flex justify-center items-center gap-1">
                    <HomeIconOutline className="w-6 mr-2 flex-shrink-0" />
                    <h3 className="text-normal md:text-lg font-semibold">
                      Tu casa frente al río
                    </h3>
                  </div>
                  <span className="text-sm md:text-base">Emplazado en la zona más emergente de la ciudad de Paraná,
                    a 15 minutos del centro de la misma con dos accesos directos,
                    uno por Blas Parera y el otro por el acceso norte.</span>
                </Alert>
              }
              <iframe src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d19277.17509677658!2d-60.48981046523429!3d-31.714625134745653!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzHCsDQyJzE2LjYiUyA2MMKwMjgnMjkuMyJX!5e1!3m2!1sen!2sar!4v1746643617521!5m2!1sen!2sar"
                title="Ubicación de López Jordan"
                width={width}
                height="450"
                style={{ border: "0" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade">
              </iframe>
            </div>
          }
          {selectedLocation === FixedLocation.JUJUY &&
            <div className="flex flex-col lg:flex-row items-center gap-5">
              {isTablet &&
                <Alert className="bg-primary border-primary text-primary-content flex flex-col gap-1 mx-4 sm:mx-auto max-w-xs sm:max-w-lg animate-fade-in-up opacity-0 transition-opacity duration-1000">
                  <div className="flex justify-center items-center gap-1 mb-4">
                    <BuildingOffice2Icon className="w-6 mr-2 flex-shrink-0" />
                    <h3 className="text-normal md:text-lg font-semibold">
                      Departamentos céntricos a la venta
                    </h3>
                  </div>
                  <span className="text-sm md:text-base">
                    Ubicados estratégicamente en la zona más alta de la ciudad, a minutos del centro, hospitales y de los comercios más importantes de la ciudad.
                  </span>
                </Alert>
              }
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19271.895459210613!2d-60.53361099927016!3d-31.740009344478086!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95b44d9239959ad1%3A0xf504b262beceeb7c!2sJujuy%20%26%20Olegario%20Victor%20Andrade%2C%20E3100%20Paran%C3%A1%2C%20Entre%20R%C3%ADos!5e1!3m2!1sen!2sar!4v1747583244085!5m2!1sen!2sar"
                title="Ubicación de Jujuy"
                width={width}
                height="450"
                style={{ border: "0" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade">
              </iframe>
              {!isTablet && <ChatBubble className="max-w-lg mx-auto animate-fade-in-up opacity-0 transition-opacity duration-1000">
                <ChatBubble.Message color="primary">
                  <div className="flex justify-center items-center gap-1 mb-4">
                    <BuildingOffice2Icon className="w-6 mr-2 flex-shrink-0" />
                    <h3 className="text-normal md:text-lg font-semibold">
                      Departamentos céntricos a la venta
                    </h3>
                  </div>
                  <span className="text-sm md:text-base">
                    Ubicados estratégicamente en la zona más alta de la ciudad, a minutos del centro, hospitales y de los comercios más importantes de la ciudad.
                  </span>
                </ChatBubble.Message>
              </ChatBubble>}
            </div>
          }
        </div>
      </div>
    </section >
  )
}

const ContactUs = () => {
  const [name, setName] = useState("")
  const [message, setMessage] = useState("")

  const isValid = name && message

  return (
    <section className="py-12" id="contact-us">
      <div className="container mx-auto px-4 sm:px-8 text-base-100">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Contactanos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <ChatBubble end style={{ placeItems: "center" }}>
            <ChatBubble.Message color="neutral" className="max-w-lg text-black">
              <div className="flex justify-center items-center gap-1 mb-4">
                <PhoneIcon className="w-5 mr-2 flex-shrink-0" />
                <h3 className="text-normal md:text-lg font-semibold">
                  Información de Contacto
                </h3>
              </div>
              <div className="text-sm md:text-base flex flex-col gap-2">
                <a
                  className="flex items-center link hover:text-gray-500"
                  href={`https://wa.me/5493433022876?text=Hola, quiero contactarme.`}
                  target="_blank"
                >
                  <DevicePhoneMobileIcon className="w-5 mr-2 flex-shrink-0" />
                  +54 343 3 022876
                </a>
                <a href="mailto:informacion@tierrasdelparana.com.ar" className="link hover:text-gray-500 flex items-center">
                  <EnvelopeIcon className="w-5 mr-2 flex-shrink-0" />
                  informacion@tierrasdelparana.com.ar
                </a>
                <p className="mb-2 flex items-center">
                  <BuildingOffice2Icon className="w-5 mr-2 flex-shrink-0" />
                  Andrade 109, Paraná, Argentina
                </p>
              </div>
            </ChatBubble.Message>
          </ChatBubble>
          <form className="space-y-4">
            <div>
              <input
                id="name"
                type="text"
                placeholder="Nombre"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <textarea
                id="message"
                placeholder="Motivo o mensaje"
                className="w-full px-4 py-2 border rounded-lg h-32 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <a
              className={`w-full btn ${isValid ? "btn-secondary" : "btn-neutral"}`}
              href={`https://wa.me/5493433022876?text=Hola, soy ${name.trim()}, me comunico por: ${message.trim()}`}
              onClick={(e) => {
                if (!isValid)
                  e.preventDefault()
              }}
              target="_blank"
            >
              Quiero contactarme
            </a>
          </form>
        </div>
      </div>
    </section>
  )
}

export default HomePage
